import { useState, useRef } from "react";
import { Zap, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCashIn } from "@/hooks/useCashIn";
import { emitXpPopup } from "@/components/ui/xp-popup";
import { playXpGain, playLevelUp } from "@/lib/sounds";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { formatError } from "@/lib/utils";

export function CashInCard() {
  const { items, totalXp, isLoading, isFetching, cashIn } = useCashIn();
  const [expanded, setExpanded] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Hide while loading OR immediately after a successful claim (don't wait for cache refresh)
  if (isLoading || claimed || items.length === 0) return null;

  const handleCashIn = () => {
    const ids = items.map((i) => i.id);
    cashIn.mutate(ids, {
      onSuccess: (result) => {
        setClaimed(true); // immediately collapse card so button can't be hit again
        toast.success(`Cashed in ${result.count} tasks for ${result.xpAwarded} XP!`);

        // XP popup at the button position
        if (btnRef.current) {
          const rect = btnRef.current.getBoundingClientRect();
          emitXpPopup(result.xpAwarded, rect.left + rect.width / 2, rect.top);
        } else {
          emitXpPopup(result.xpAwarded, window.innerWidth / 2, window.innerHeight / 2);
        }

        // Sound
        playXpGain(result.xpAwarded);
        if (result.leveledUp) {
          setTimeout(() => playLevelUp(), 300);
        }

        // Big confetti burst
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
          colors: ["#22c55e", "#eab308", "#3b82f6", "#a855f7"],
        });
      },
      onError: (err) => toast.error(formatError(err)),
    });
  };

  return (
    <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-green-500/20 flex items-center justify-center">
            <Zap className="h-4 w-4 text-green-500" />
          </div>
          <div>
            <p className="text-sm font-semibold">
              {items.length} resolved {items.length === 1 ? "task" : "tasks"} ready
            </p>
            <p className="text-xs text-muted-foreground">
              {totalXp} XP waiting to be claimed
            </p>
          </div>
        </div>
        <Button
          ref={btnRef}
          onClick={handleCashIn}
          disabled={cashIn.isPending || isFetching}
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
        >
          {cashIn.isPending || isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Zap className="h-3.5 w-3.5" />
          )}
          Cash In {totalXp} XP
        </Button>
      </div>

      {/* Expandable task list */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {expanded ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
        {expanded ? "Hide" : "Show"} tasks
      </button>

      {expanded && (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-background/50"
            >
              <span className="truncate mr-2">{item.title}</span>
              <div className="flex items-center gap-2 shrink-0">
                {item.project_tag && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {item.project_tag}
                  </Badge>
                )}
                <span className="text-green-500 font-medium">
                  +{item.xp_value} XP
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
